import React, { useState, useEffect } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import MobileNetworkAnimation from "./MobileAnimation";
import AnimatedMetrics from "./AnimatedMetrics";

export default function HowItWorks() {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const metricSets = [
    ["Heart-rate variability", "Resting HR trends", "Sleep fragmentation", "Movement patterns"],
    ["GAD-7", "PHQ-9", "Side-effects", "Medication adherence"],
    ["Sleep quality", "Stress episodes", "Circadian rhythm shifts", "Physical activity"]
  ];

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % metricSets.length);
    }, 5005);
    return () => clearInterval(id);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "1100px",
        mx: "auto",
        px: { xs: 2, sm: 3, md: 6 },
        py: { xs: 4, md: 6 }
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 540,
          textAlign: { xs: "center", md: "left" },
          mb: { xs: 4, md: 5 }
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 4,
            borderRadius: "2px",
            background: "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
            mb: 3,
            mx: { xs: "auto", md: 0 }
          }}
        />

        <Typography
          variant="overline"
          sx={{
            textTransform: "uppercase",
            letterSpacing: "1.4px",
            color: "#6366f1",
            fontWeight: 600,
            opacity: 0.9,
            fontSize: { xs: "0.72rem", sm: "0.78rem" },
            mb: 1.5,
            display: "inline-block"
          }}
        >
          Collect · Synthesize · Alert
        </Typography>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "#0f172a",
              fontSize: { xs: "1.85rem", sm: "2rem", md: "2.25rem" },
              lineHeight: { xs: 1.28, md: 1.22 },
              letterSpacing: "-0.015em",
              mb: { xs: 2, md: 3 },
              px: { xs: 1.5, sm: 0 }
            }}
          >
            How effectively is my treatment working?
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.25, duration: 1.1, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "#334155",
              fontSize: { xs: "1rem", sm: "1.05rem" },
              lineHeight: { xs: 1.65, sm: 1.75 },
              mb: { xs: 3.5, md: 4 },
              maxWidth: { xs: "100%", md: "92%" },
              px: { xs: 1, sm: 0 },
              opacity: 0.95
            }}
          >
            Panacea converts weeks of unseen patient data between visits into interpretable
            signals — alerting clinicians the moment a treatment begins to drift off course.
          </Typography>
        </motion.div>

        <AnimatedMetrics />
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 3, sm: 4, md: 6 },
          flexWrap: "wrap"
        }}
      >
        <MobileNetworkAnimation />
      </Box>
    </Box>
  );
}
